import React, { useCallback, useEffect, useRef, useState } from 'react';

import Tippy from '@tippyjs/react';

import { cn } from 'lib/helpers/utility';

import Hint from 'components/shared/shared/Hint';
import Button from 'components/shared/Button/Button';
import SegmentedControl from 'components/shared/SegmentedControl/SegmentedControl';

import DrawSignature from './components/DrawSignature';
import TypeSignature from './components/TypeSignature';
import TypefaceDropdown from './components/TypefaceDropdown';
import { useContainerWidth } from './hooks/useContainerWidth';
import { loadSignatureFonts } from './lib/loadSignatureFonts';
import { SIGNATURE_HINT, SIGNATURE_TABS, TYPE_SIGNATURE_FONTS } from './lib/constants';

/**
 * @param {import('./lib/propTypes').SignatureProps} props
 */
const Signature = ({
  value,
  onChange,
  label = 'Your signature',
  penColor = '#000000',
  canvasHeight = 200,
  hint = true,
  defaultName = '',
  className = '',
  tabsSize,
  'data-qa': dataQa = ''
}) => {
  const [mode, setMode] = useState('draw');
  const [fontId, setFontId] = useState(TYPE_SIGNATURE_FONTS[0].id);
  const [fontsReady, setFontsReady] = useState(false);
  const [drawHistory, setDrawHistory] = useState({ canUndo: false, canRedo: false });
  /** @type {React.MutableRefObject<{ getValue: () => string | null, clear: () => void, undo: () => string | null, redo: () => string | null } | null>} */
  const drawRef = useRef(null);
  /** @type {React.MutableRefObject<{ getValue: () => string | null, clear: () => void } | null>} */
  const typeRef = useRef(null);
  const [canvasAreaRef, containerWidth] = useContainerWidth();
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let cancelled = false;
    loadSignatureFonts().then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const emitChange = useCallback((next) => onChangeRef.current?.(next), []);

  const handleTabChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    const nextValue =
      nextMode === 'draw' ? drawRef.current?.getValue?.() : typeRef.current?.getValue?.();
    emitChange(nextValue || null);
  };

  const handleUndo = () => {
    if (mode !== 'draw' || !drawRef.current?.undo) return;
    emitChange(drawRef.current.undo());
  };

  const handleRedo = () => {
    if (mode !== 'draw' || !drawRef.current?.redo) return;
    emitChange(drawRef.current.redo());
  };

  const handleClear = () => {
    if (mode === 'draw') {
      drawRef.current?.clear?.();
    } else {
      typeRef.current?.clear?.();
    }
    emitChange(null);
  };

  const canUndo = mode === 'draw' && drawHistory.canUndo;
  const canRedo = mode === 'draw' && drawHistory.canRedo;

  return (
    <div className={cn('flex min-w-0 flex-col gap-3', className)}>
      <SegmentedControl
        items={SIGNATURE_TABS}
        active={mode}
        size={tabsSize}
        disableOverflow
        onClick={(item) => handleTabChange(item.value)}
      />

      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 text-sm text-neutral-500">
          <span className="truncate text-sm font-500 text-neutral-800">{label}</span>
          {mode === 'draw' && (
            <>
              <Tippy className="tippy-dark" content="Undo" placement="bottom" touch={false}>
                <div className="flex items-center">
                  <Button
                    variant="link"
                    size="small"
                    color="neutral"
                    icon="undo-mono"
                    className="!h-11 !w-11"
                    aria-label="Undo signature stroke"
                    onClick={handleUndo}
                    isDisabled={!canUndo}
                  />
                </div>
              </Tippy>
              <Tippy className="tippy-dark" content="Redo" placement="bottom" touch={false}>
                <div className="flex items-center">
                  <Button
                    variant="link"
                    size="small"
                    color="neutral"
                    icon="redo-mono"
                    className="!h-11 !w-11"
                    aria-label="Redo signature stroke"
                    onClick={handleRedo}
                    isDisabled={!canRedo}
                  />
                </div>
              </Tippy>
            </>
          )}
        </div>
        {mode === 'draw' ? (
          <Button
            variant="link"
            size="small"
            color="neutral"
            text="Clear"
            className="!h-11 !px-2"
            aria-label="Clear signature"
            onClick={handleClear}
          />
        ) : (
          <div className="flex items-center gap-1 text-sm text-neutral-600">
            <span>Typeface:</span>
            <TypefaceDropdown fontId={fontId} onChange={setFontId} />
          </div>
        )}
      </div>

      <div
        ref={canvasAreaRef}
        style={{ height: canvasHeight }}
        className="relative w-full min-w-0 overflow-hidden">
        <div className={mode === 'draw' ? 'block' : 'hidden'}>
          <DrawSignature
            active={mode === 'draw'}
            value={mode === 'draw' ? value : null}
            onChange={emitChange}
            onHistoryChange={setDrawHistory}
            penColor={penColor}
            canvasHeight={canvasHeight}
            containerWidth={containerWidth}
            handleRef={drawRef}
            data-qa={dataQa}
          />
        </div>
        <div className={mode === 'type' ? 'block' : 'hidden'}>
          <TypeSignature
            active={mode === 'type'}
            onChange={emitChange}
            canvasHeight={canvasHeight}
            containerWidth={containerWidth}
            fontId={fontId}
            fontsReady={fontsReady}
            defaultName={defaultName}
            handleRef={typeRef}
            data-qa={dataQa}
          />
        </div>
      </div>

      {hint && <Hint hint={SIGNATURE_HINT} />}
    </div>
  );
};

export default Signature;
